import { ShapeComponents, type ShapeType } from "./index";

/** Default stroke width for canvas shape nodes (thick enough to be visible). */
const DEFAULT_STROKE_WIDTH = 2.5;

interface ShapeRendererProps {
  type: ShapeType;
  width: number;
  height: number;
  fill: string;
  fillOpacity?: number;
  stroke: string;
  /** Override stroke width — use a smaller value (e.g. 1) for icon previews. */
  strokeWidth?: number;
  strokeDasharray?: string;
  className?: string;
  /**
   * When true (default for node rendering), the SVG is absolutely positioned
   * to fill the parent container. Set to false for inline icon use.
   */
  absolute?: boolean;
}

/**
 * Renders the SVG for a canvas shape with stroke-inset correction.
 *
 * SVG strokes are centered on path boundaries — without adjustment half the
 * stroke is clipped. We offset all geometry by strokeWidth and reduce the
 * inner dimensions accordingly so the full stroke is always visible within
 * the node's bounding box.
 */
export function Shape({
  type,
  width,
  height,
  fill,
  fillOpacity = 1,
  stroke,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  strokeDasharray,
  className,
  absolute = true,
}: ShapeRendererProps) {
  const ShapeComponent = ShapeComponents[type];
  if (!ShapeComponent || !width || !height) return null;

  const sw = strokeWidth;
  const innerWidth = Math.max(0, width - 2 * sw);
  const innerHeight = Math.max(0, height - 2 * sw);

  return (
    <svg
      width={width}
      height={height}
      className={className}
      style={
        absolute
          ? { position: "absolute", inset: 0, overflow: "visible" }
          : { display: "block", overflow: "visible" }
      }
    >
      <g transform={`translate(${sw}, ${sw})`}>
        <ShapeComponent
          width={innerWidth}
          height={innerHeight}
          fill={fill}
          fillOpacity={fillOpacity}
          stroke={stroke}
          strokeWidth={sw}
          strokeDasharray={strokeDasharray}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}
